import * as React from 'react';
import { Box } from '@lcifr/components/box';
import { Wrap as LuciferWrap } from '@lcifr/components/wrap';
import { Image } from 'components/Image';
import { sx } from '@lcifr/sx';
import { Heading } from '@lcifr/components/heading';
import { useCollection, useRawI18n } from '@lcifr/pages';
import { Button } from '@lcifr/components/button';
import { Icon as IconLucifer } from '@lcifr/components/icon';
import { Stack } from '@lcifr/components/stack';
import { LinkStudio } from '@lcifr/components/link';
import { Section } from '@lcifr/components/section';
import { useScroll } from 'utils/hooks/useScroll';
import Link from 'next/link';
import { Anchor } from '@lcifr/components/anchor';
import { HeroProductContext } from './HeroProduct.context';
import { Inline } from '@lcifr/components/inline';
import { CollectionType } from '@lcifr/components/collection';

export const Stage = sx(Box)({
  position: 'relative',
});

export const Bg = sx(Box)(({ fadeOut }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  width: '100%',
  opacity: fadeOut ? 0 : 1,
  transition: '0.5s opacity ease-out',
  background:
    'linear-gradient(180deg, rgba(15, 17, 37, 0.1) 0%, rgba(15, 17, 37, 0.03) 100%)',
}));

export const Icon = sx(IconLucifer)(({ fadeOut }) => ({
  opacity: fadeOut ? 0 : 1,
  transition: '0.2s opacity ease-out',
  bg: 'background',
  p: [4, 4, 6],
  borderRadius: '50%',
  cursor: 'pointer',
  svg: {
    height: [24, 24, 36],
    width: [24, 24, 36],
  },
  boxSizing: 'border-box',
  boxShadow: '4px 4px 4px rgba(0,0,0,0.02)',
}));

const getUrlFromId = (ref) => {
  if (!ref) return '';
  const [_file, id, extension] = ref.split('-');
  return `https://cdn.sanity.io/files/${process.env.NEXT_PUBLIC_STUDIO_PROJECT_ID}/${process.env.NEXT_PUBLIC_STUDIO_DATASET}/${id}.${extension}/dl`;
};

type HeroProductProps = {
  headline?: any;
  intro?: any;
  image?: any;
  width?: any;
  minHeight?: any;
  bg?: any;
  bgImage?: any;
  spaceT?: any;
  spaceB?: any;
  stackSpace?: any;
  id?: any;
  vRhythm?: any;
  downloadList?: any;
  downloadTextGlobal?: any;
  pageId?: any;
};

export function HeroProduct({
  headline,
  intro,
  image,
  width,
  minHeight,
  bg,
  bgImage,
  spaceT,
  spaceB,
  stackSpace,
  id,
  vRhythm,
  downloadList,
  downloadTextGlobal,
  pageId,
}: HeroProductProps) {
  const [isTop, setIsTop] = React.useState(false);
  useScroll({
    onScroll: (scrollPosition) => {
      setIsTop(scrollPosition.y >= 1 ? true : false);
    },
    active: true,
  });

  const heroContext = React.useContext(HeroProductContext);
  React.useEffect(() => {
    requestAnimationFrame(() => {
      heroContext.setActive();
    });
  }, [heroContext]);

  const heroDirection =
    heroContext.direction === 'right' ? 'translateX(10%)' : 'translateX(-10%)';

  const pageConfig = useRawI18n();
  const backText = pageConfig?.pageTechnology?.backText;
  const backLink = pageConfig?.pageTechnology?.backLink;
  const productList = pageConfig?.heroProduct?.productList;

  const productListCollection = useCollection(productList).filter(
    ({ _id }) => !_id.startsWith('drafts.'),
  );

  const index = productListCollection.findIndex(
    (product) => product._id === pageId,
  );

  const prevIndex = index > 0 ? index - 1 : productListCollection?.length - 1;
  const nextIndex = index < productListCollection?.length - 1 ? index + 1 : 0;
  const prevProduct = productListCollection[prevIndex];
  const nextProduct = productListCollection[nextIndex];

  return (
    <Box sx={{ '> div > div': { pl: '0 !important', pr: '0 !important' } }}>
      <Section
        width="full"
        minHeight={minHeight}
        bg={bg}
        bgImage={bgImage}
        spaceT={spaceT || 0}
        spaceB={spaceB || 8}
        stackSpace={0}
        id={id}
        vRhythm={vRhythm}
      >
        <Box
          sx={{
            height: '120vh',
          }}
        >
          <Box
            sx={{
              position: 'sticky',
              top: 0,
            }}
          >
            <Stage>
              <Bg fadeOut={isTop} />
              <LuciferWrap width="normal">
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateRows: '3fr 1fr',
                    height: [
                      'calc(100vh - 64px)',
                      'calc(100vh - 64px)',
                      'calc(100vh - 30px)',
                    ],
                    maxHeight: '1080px',
                  }}
                >
                  <Box
                    sx={{
                      height: [
                        'calc(100% - 96px)',
                        'calc(100% - 96px)',
                        'calc(100% - 140px)',
                      ],
                      alignSelf: 'end',
                      position: 'relative',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Image
                      sx={{
                        verticalAlign: 'bottom',
                        img: {
                          objectFit: 'contain !important',
                          transform: heroContext?.isActive
                            ? 'translateX(0)'
                            : heroDirection,
                          opacity: heroContext?.isActive ? 1 : 0,
                          transition: '0.4s all ease-out',
                        },
                      }}
                      className="heroProductImage"
                      image={image}
                      alt="product"
                    />
                  </Box>
                  <Box
                    sx={{
                      alignSelf: 'center',
                      height: 'auto',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: ['column', 'column', 'row'],
                        justifyContent: 'space-between',
                        alignItems: ['flex-start', 'flex-start', 'flex-start'],
                        gap: ['8px', '8px', '24px'],
                        pb: 4,
                        flexShrink: 0,
                      }}
                    >
                      <Box>
                        <Stack space={3}>
                          <Stack space={3}>
                            <Inline>
                              <LinkStudio link={backLink}>
                                <Button
                                  variant="strippedPrimary"
                                  sx={{
                                    fontSize: '16px !important',
                                    textTransform: 'uppercase',
                                    letterSpacing: '3px',
                                  }}
                                >
                                  <IconLucifer
                                    name="chev-left"
                                    sx={{ mr: 2, color: 'primary' }}
                                  />
                                  {backText}
                                </Button>
                              </LinkStudio>
                            </Inline>
                            <Heading variant="h1">{headline}</Heading>
                          </Stack>
                          <Box
                            sx={{
                              height: isTop ? 120 : 0,
                              pointerEvents: isTop ? 'auto' : 'none',
                              opacity: isTop ? 1 : 0,
                              transition: '0.5s all ease-out',
                            }}
                          >
                            <Stack space={3}>
                              {intro && (
                                <Heading variant="h4" sx={{ fontWeight: 400 }}>
                                  {intro}
                                </Heading>
                              )}

                              {downloadList && (
                                <CollectionType node={downloadList}>
                                  {(downloads) => {
                                    return (downloads as any)?.[0]?.file?.asset
                                      ?._ref ? (
                                      <Link
                                        // key={`link-${i}`}
                                        href={getUrlFromId(
                                          (downloads as any)?.[0]?.file?.asset
                                            ?._ref,
                                        )}
                                        passHref
                                        legacyBehavior
                                      >
                                        <Anchor>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              gap: '6px',
                                            }}
                                          >
                                            <IconLucifer
                                              name="clip"
                                              sx={{
                                                svg: {
                                                  width: '24px',
                                                  height: '24px',
                                                },
                                              }}
                                            />
                                            <Box
                                              sx={{
                                                fontWeight: 500,
                                                fontSize: '16px',
                                                color: 'primary',
                                              }}
                                            >
                                              {downloadTextGlobal}
                                            </Box>
                                          </Box>
                                        </Anchor>
                                      </Link>
                                    ) : (
                                      <Box />
                                    );
                                  }}
                                </CollectionType>
                              )}
                            </Stack>
                          </Box>
                        </Stack>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 20,
                          width: '100%',
                          justifyContent: [
                            'space-between',
                            'space-between',
                            'flex-end',
                          ],
                        }}
                      >
                        <Box sx={{ width: 'auto', flexShrink: 0 }}>
                          <Link
                            href={{
                              pathname: `/${prevProduct?.slug}`,
                            }}
                            passHref
                            legacyBehavior
                          >
                            <Anchor
                              onClick={() => {
                                heroContext.setNewDirection('left');
                              }}
                            >
                              <Icon fadeOut={isTop} name="chev-left" />
                            </Anchor>
                          </Link>
                        </Box>

                        <Box sx={{ width: 'auto', flexShrink: 0 }}>
                          <Link
                            href={{
                              pathname: `/${nextProduct?.slug}`,
                            }}
                            passHref
                            legacyBehavior
                          >
                            <Anchor
                              onClick={() => {
                                heroContext.setNewDirection('right');
                              }}
                            >
                              <Icon fadeOut={isTop} name="chev-right" />
                            </Anchor>
                          </Link>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </LuciferWrap>
            </Stage>
          </Box>
        </Box>
      </Section>
    </Box>
  );
}
